import styled from "@emotion/styled";
import { SelectChangeEvent } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  CameraIcon,
  ClipboardBulletIcon,
  MiniTractorIcon,
  TrashCanIcon,
  WarningIcon,
  WarningModalIcon,
} from "../assets/icons/icons";
import usePostMobility from "../hooks/usePostMobility";
//import userStorage from "../utils/userStorage";
import MultipleModal from "./multipleModal";
import AlertModal from "./alertModal";
import SelectBox, { menuItemType } from "./selectBox";
import theme from "../theme";
import useGetAllCustomer from "../hooks/useGetAllCustomer";
import { /*isMobile,*/ isOnlyNumber } from "../utils/util";
import { CustomAlert } from "./customAlert";
import { mobileOptions } from "../utils/constants";
import { Button } from "./common/Button";
import { ModalLayout } from "./common/ModalLayout";
import { useSnackbar } from "notistack";
import { useWindowSize } from "@reactuses/core";
import { putMobileImage } from "../api/mobility";
import { deviceImageArray } from "../utils/deviceImageArray";
import { TextInput } from "./common/TextInput";
import { Label } from "./common/Label";
import { useI18n } from "../locales";

const { sm, md, lg } = theme.screen;

type Params = {
  mobility_image: string;
  mobility_name: string;
  phone: string;
  mobility_type_code: string;
  device_pid: string;
  device_phone: string | null;
};

const MobilityRegisterModal: React.FC<{ open: boolean; onClose: any }> = (
  props
) => {
  const { t } = useI18n();
  const { open, onClose } = props;
  const isOnPremise = process.env.REACT_APP_ROUTE_ID; // 온프레미스 여부 (true면 IMEI 항목 지우고 device_phone에 초기값 지정)
  const { width } = useWindowSize();
  const { enqueueSnackbar } = useSnackbar();
//  const user = userStorage.get();
  const queryClient = useQueryClient();
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const [imgFile, setImgFile] = useState("");
  const [imgData, setImgData] = useState<any>();
  const [input, setInput] = useState<Params>({
    mobility_image: "",
    mobility_name: "",
    phone: "-1", // 소유자
    mobility_type_code: "0",
    device_pid: "", // 시리얼 번호
    device_phone: isOnPremise ? "000000000000000" : "", // IMEI
  });
  const [openCancelmModal, setOpenCancelModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openMultipleModal, setOpenMultipleModal] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const handleFileUpload = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };
  const handleFileCancel = () => {
    setInput((prevState) => ({
      ...prevState,
      mobility_image: "",
    }));
    setImgFile("");
    setImgData("");
  };

  const handleFileInputChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      // console.log(file); // Create a FormData object to send the file to the server
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        if (reader.result !== null) {
          setImgFile(reader.result as string);
        }
      };

      setImgData(file);
    }
  };

  const { mutate, failureReason, isPending, error } = usePostMobility();

  const handlePost = () => {
    mutate(
      {
        mobility_name: input.mobility_name,
        phone: input.phone.replaceAll("-", ""), // 소유자
        mobility_type_code: Number(input.mobility_type_code),
        device_pid: input.device_pid,
        device_phone: input.device_phone,
      },
      {
        onSuccess: async (postResponse) => {
          let imageSuccess = true; // 이미지 작업 성공 여부
          let imageMessage = ""; // 이미지 작업 메시지

          try {
            if (imgData) {
              // 이미지 업로드
              const formData = new FormData();
              formData.append("pid", postResponse?.device_pid as string);
              formData.append("file", imgData);

              const response = await putMobileImage(formData);

              if (response.code === 2002) {
                imageMessage = response.message;
              } else {
                imageSuccess = false;
                imageMessage = response.message;
              }
            }
          } catch (error) {
            imageSuccess = false;
            imageMessage = t.deviceRegister.imageUploadError;
          }

          // 메시지 처리
          enqueueSnackbar(postResponse.message, { variant: "success" });

          if (imageMessage) {
            enqueueSnackbar(imageMessage, {
              variant: imageSuccess ? "success" : "error",
            });
          }

          // 최종 정리 및 초기화
          finalizePost();
        },
      }
    );
  };

  const finalizePost = () => {
    setOpenConfirmModal(false);
    setImgData("");
    closeToModal();
    queryClient.invalidateQueries({ queryKey: ["Mobility"] });
  };

  const onPressNo = () => {
    setOpenConfirmModal(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // 기기 종류 선택
  const handleSelectBox = (event: SelectChangeEvent) => {
    setInput((prevState) => {
      return {
        ...prevState,
        mobility_type_code: event.target.value.toString(),
      };
    });
  };

  const onClickBtn = () => {
    setOpenConfirmModal(true);
  };

  useEffect(() => {
    if (failureReason !== null) {
      setOpenErrorModal(true);
    }
  }, [failureReason]);

  // 소유자 리스트 가져오기
  const { data: AllCustomer } = useGetAllCustomer({
    page: 0,
    searchKey: "",
    area: "",
    limit: -1,
  });
  let relatedIdListType: menuItemType[] = [
    {
      value: -1,
      title: t.deviceRegister.ownerPlaceholder,
    },
  ];
  if (AllCustomer) {
    AllCustomer.list.filter((item) => {
      relatedIdListType.push({
        value: item.id,
        title: item.seq + ". " + item.name,
      });
    });
  }

  // 소유자 선택
  const handleSelectBoxUser = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    if (value == "-1") {
      setOpenAlert(true);
      return;
    } else {
      setInput((prevState) => ({
        ...prevState,
        phone: event.target.value,
      }));
    }
  };

  // 모달 닫기 함수
  const closeToModal = () => {
    setOpenCancelModal(false);
    setImgFile("");
    onClose();
    setImgData("");
    setInput({
      mobility_image: "",
      mobility_name: "",
      phone: "-1", // 소유자
      mobility_type_code: "0",
      device_pid: "",
      device_phone: "",
    });
  };

  const isDevicePidErr = !isOnlyNumber(input.device_pid);
  const isDevicePhoneErr = !isOnlyNumber(input.device_phone ?? "");

  // 필수입력값들
  const isAllFilled =
    [
      input.mobility_name,
      // input.phone,
      input.mobility_type_code,
      input.device_pid,
      input.device_phone,
    ].every((el) => el !== "") &&
    input.phone !== "-1" &&
    input.device_pid.length >= 8 &&
    input.device_phone &&
    input.device_phone?.length >= 15 &&
    !isDevicePidErr &&
    !isDevicePhoneErr;

  return (
    <ModalLayout
      open={open}
      onClose={() => setOpenCancelModal(true)}
      width="550px"
      title={t.deviceRegister.title}
    >
      <Body>
        <input
          style={{ display: "none" }}
          type="file"
          ref={fileUploadRef}
          accept=".jpg,.jpeg,.png"
          onChange={handleFileInputChange}
        />
        <MiddleBody>
          <ImageWrapper
            style={{
              background:
                imgFile === ""
                  ? theme.color.white
                  : theme.color.neutral_50,
              position: "relative",
            }}
          >
            {imgFile ? (
              <CustomImg src={imgFile} alt="농기계 이미지" />
            ) : (
              <CustomImg
                src={`${process.env.REACT_APP_BASE_URL}/img/${
                  deviceImageArray.find(
                    (list) => list.code === input.mobility_type_code
                  )?.detailFileName
                }`}
                alt="농기계 이미지"
              />
            )}
            {imgFile ? (
              <IconWrapper2 onClick={handleFileCancel}>
                <TrashCanIcon />
              </IconWrapper2>
            ) : null}
            {input.mobility_type_code === "0" && (
              <IconWrapper onClick={handleFileUpload}>
                <CameraIcon />
              </IconWrapper>
            )}
          </ImageWrapper>

          <InputWrapper>
            <Label text={t.deviceRegister.deviceName} htmlFor="mobility_name" isRequired />
            <TextInput
              placeholder={t.deviceRegister.deviceNamePlaceholder}
              onChange={handleChange}
              width={"370px"}
              id="mobility_name"
              name="mobility_name"
              value={input.mobility_name}
              maxLength={20}
            />
          </InputWrapper>

          <InputWrapper>
            <Label text={t.deviceRegister.owner} htmlFor="phone" isRequired />

            <SelectBox
              filter={input?.phone}
              menuItems={relatedIdListType}
              width={width <= md ? `100%` : 370}
              handleChange={handleSelectBoxUser}
              mr={"0px"}
              srLabel={t.deviceRegister.selectOwner}
            />
          </InputWrapper>

          <InputWrapper>
            <Label
              text={t.deviceRegister.deviceType}
              htmlFor="mobility_type_code"
              isRequired
            />

            <SelectBox
              filter={Number(input.mobility_type_code)}
              handleChange={handleSelectBox}
              menuItems={mobileOptions}
              width={width <= md ? `100%` : 370}
              mr={"0px"}
              srLabel={t.deviceRegister.deviceType}
            />
          </InputWrapper>

          <InputWrapper
            style={{
              position: "relative",
            }}
          >
            <Label
              text={t.deviceRegister.terminalSerialNumber}
              htmlFor="device_pid"
              isRequired
            />

            <TextInput
              onChange={handleChange}
              width={"370px"}
              id="device_pid"
              name="device_pid"
              value={input.device_pid}
              placeholder={t.deviceRegister.terminalSerialPlaceholder}
              maxLength={16}
            />
            {isDevicePidErr && <PwError>{t.deviceRegister.numbersOnly}</PwError>}
          </InputWrapper>
          {!isOnPremise && (
            <InputWrapper
              style={{
                position: "relative",
              }}
            >
              <Label
                text={t.deviceRegister.terminalIMEI}
                htmlFor="device_phone"
                isRequired
              />
              <TextInput
                onChange={handleChange}
                width={"370px"}
                id="device_phone"
                name="device_phone"
                value={input.device_phone ?? ""}
                placeholder={t.deviceRegister.terminalIMEIPlaceholder}
                maxLength={15}
              />
              {isDevicePhoneErr && <PwError>{t.deviceRegister.numbersOnly}</PwError>}
            </InputWrapper>
          )}
        </MiddleBody>
      </Body>

      {/* 동시 등록 조건 : user의 grade == 2 */}
      {/*NMS 설치앱을 이용한 등록 프로세스로 인해 비활성화 처리
      {user?.grade == 2 && (
        <MultiRegiBtnContainer>
          <Button
            bgColor={theme.color.aquamarine_500}
            hoverColor={theme.color.aquamarine_600}
            onClick={() => {
              if (isMobile()) {
                alert(`여러 기기 등록하기는 웹에서만 사용이 가능합니다.`);
                return;
              }
              setOpenMultipleModal(true);
            }}
            style={{
              height: "30px",
              fontSize: "12px",
            }}
          >
            여러 기기 동시에 등록하기
          </Button>
        </MultiRegiBtnContainer>
      )} */}

      <BtnWrapper>
        <Button
          isDisabled={!isAllFilled}
          isLoading={isPending}
          bgColor={
            isAllFilled
              ? theme.statusColor.MAIN
              : theme.statusColor.DISABLED
          }
          onClick={onClickBtn}
          hoverColor={theme.statusColor.MAIN_HOVER}
        >
          {width <= md ? t.deviceRegister.noRegisterButton : t.deviceRegister.registerButton}
        </Button>
        <Button
          bgColor={theme.statusColor.CLOSE}
          hoverColor={theme.statusColor.CLOSE_HOVER}
          onClick={() => setOpenCancelModal(true)}
        >
          {t.deviceRegister.registerCancel}
        </Button>
      </BtnWrapper>

      <AlertModal
        open={openConfirmModal}
        onPressNo={onPressNo}
        title={t.deviceRegister.confirmRegister}
        content={t.deviceRegister.confirmRegisterContent}
        confirmText={t.deviceRegister.confirmRegisterButton}
        onPressYes={() => handlePost()}
        icon={<ClipboardBulletIcon />}
      />
      <AlertModal
        open={openErrorModal}
        onPressNo={() => {
          setOpenErrorModal(false);
          onPressNo();
        }}
        title={t.deviceRegister.checkInput}
        content={error?.response?.data.message ?? ""}
        confirmText={t.common.confirm}
        onPressYes={() => {
          setOpenErrorModal(false);
          onPressNo();
        }}
        icon={<WarningModalIcon />}
      />
      <AlertModal
        open={openCancelmModal}
        onPressNo={() => setOpenCancelModal(false)}
        title={t.deviceRegister.cancelRegister}
        content={t.deviceRegister.cancelRegisterContent}
        confirmText={t.common.confirm}
        icon={<WarningIcon />}
        onPressYes={closeToModal}
      />
      <MultipleModal
        open={openMultipleModal}
        onPressNo={() => setOpenMultipleModal(false)}
        onClose={onClose}
        title={t.deviceRegister.bulkUploadTitle}
        icon={<MiniTractorIcon />}
        content={t.deviceRegister.bulkUploadContent}
      />
      <CustomAlert
        open={openAlert}
        onPressOK={() => setOpenAlert(false)}
        title={t.deviceRegister.ownerRequired}
        content={t.deviceRegister.ownerError}
        confirmText={t.common.confirm}
        icon={<WarningIcon />}
      />
    </ModalLayout>
  );
};

const Body = styled.div`
  background: ${theme.color.neutral_50};
  border-radius: 10px;
  padding-left: 40px;
  padding-right: 30px;
  padding-top: 30px;
  padding-bottom: 30px;
  width: 100%;
  display: flex;
  gap: 44px;
  margin-bottom: 40px;

  @media (max-width: ${sm}px) {
    padding: 10px;
    padding-bottom: 30px;
  }
`;

const MiddleBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 25px;
`;

const ImageWrapper = styled.div`
  width: 100%;
  max-width: 400px;
  border: 3px solid ${theme.color.neutral_200};
  display: flex;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;

  @media (min-width: ${lg + 1}px) {
    width: 400px;
  }
`;

const IconWrapper = styled.button`
  width: 43px;
  height: 43px;
  background: ${theme.color.white};
  border-radius: 50%;
  box-shadow: 0px 1.0705px 4.282px rgba(0, 0, 0, 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 15px;
  right: 15px;
  cursor: pointer;
  &:hover {
    background: ${theme.color.neutral_100};
    box-shadow: 0px 1.0705px 10px rgba(0, 0, 0, 0.2);
  }
`;
const IconWrapper2 = styled.button`
  font-weight: bold;
  color: ${theme.color.neutral_400};
  font-size: 20px;
  width: 43px;
  height: 43px;
  background: ${theme.color.white};
  border-radius: 50%;
  box-shadow: 0px 1.0705px 4.282px rgba(0, 0, 0, 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 15px;
  right: 70px;
  cursor: pointer;
  &:hover {
    background: ${theme.color.neutral_100};
    box-shadow: 0px 1.0705px 10px rgba(0, 0, 0, 0.2);
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${sm}px) {
    width: 100%;
  }
  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    width: 100%;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    width: auto;
  }
`;

const BtnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin: 0 50px;
  margin-top: 20px;

  button {
    width: 270px;
    height: 50px;
  }

  @media (max-width: ${md}px) {
    margin: 0px;
    margin-top: 20px;

    button {
      height: 30px;
    }
  }
`;

const CustomImg = styled.img`
  width: 90%;
  min-height: 200px;
  max-height: 270px;
  object-fit: contain;

  @media (min-width: ${lg + 1}px) {
    width: 247px;
    height: 270px;
  }
`;

const PwError = styled.p`
  font-size: 10px;
  color: ${theme.color.red_500};
  padding: 5px;
  position: absolute;
  bottom: -23px;
  left: 3px;
`;

/*const MultiRegiBtnContainer = styled.div`
  margin-right: 50px;
  display: flex;
  justify-content: end;

  @media (max-width: ${md}px) {
    margin: 0px;
  }
`;*/

export default MobilityRegisterModal;
